import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DataserviceService } from '../dataservice.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpParams } from '@angular/common/http';
import { AppComponent } from '../app.component';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-recruiting',
  templateUrl: './recruiting.component.html',
  styleUrl: './recruiting.component.css'
})
export class RecruitingLinkComponent implements OnDestroy {

  currentUser: any;
  recruitForm: FormGroup;
  loading: boolean = false;
  myRecruitsLoading: boolean = false;
  myRecruits: any[] = [];
  filteredRecruits: any[] = [];
  selectedRecruit: any;
  recruitModal: boolean = false;
  recruitSearchControl: FormControl = new FormControl('');
  statusFilterControl: FormControl = new FormControl('all');

  private destroy$ = new Subject<void>();

  constructor(
    private data: DataserviceService,
    private notification: NzNotificationService,
    private app: AppComponent,
  ) {

    this.recruitForm = new FormGroup({
      'first_name': new FormControl(null, [Validators.required]),
      'last_name': new FormControl(null, [Validators.required]),
      'phone': new FormControl(null, [Validators.required]),
      'email': new FormControl(null, [Validators.required]),
      'providers': new FormControl(null, [Validators.required])
    });

    this.recruitSearchControl.valueChanges
      .pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyRecruitFilter();
      });

    this.statusFilterControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value) {
          this.statusFilterControl.setValue('all', { emitEvent: false });
        }
        this.applyRecruitFilter();
      });

    this.applyRecruitFilter();
  }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user')!)
    this.getMyRecruits();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetForm() {
    this.recruitForm.reset();
  }

  submitRecruit() {
    this.loading = true;

    this.data.postData(this.recruitForm.value, `recruiting/create`)
      .subscribe({
        next: (res: any) => {
          if (res?.result == 'phone') {
            alert(res?.message);
            this.loading = false;
          }
          else if (res?.result == 'supabase') {
            alert('Failed to create user, please try again or reach out to support');
            this.loading = false;
          }
          else {
            this.notification.create(
              'success',
              'Recruit created!',
              `${this.recruitForm.value.first_name} ${this.recruitForm.value.last_name} has been created! They should receive a text message at ${this.recruitForm.value.phone} shortly...`,
              { nzPlacement: 'top' }
            );

            this.loading = false;
            this.resetForm();
          }
        },
        error: (err: any) => {
          console.error(err);
          alert('Failed to create recruit. Please try again.');
          this.loading = false;
        }
      })

  }

  getMyRecruits() {
    this.myRecruitsLoading = true;
    let params = this.setParams();

    this.data.getData(`recruiting/my-recruits`, { params })
      .subscribe({
        next: (res: any) => {
          this.myRecruits = Array.isArray(res) ? res : [];
          this.applyRecruitFilter();
          this.myRecruitsLoading = false;
          console.log('myRecruits', this.myRecruits);
        },
        error: (err: any) => {
          console.error(err);
          this.notification.create('error', 'Failed to load recruits', 'Please try again.', { nzPlacement: 'top' });
          this.myRecruitsLoading = false;
        }
      })
  }

  getRecruitInfo(r: any) {
    this.data.getData(`recruiting/my-recruit-docs/${r.id}`).subscribe({
      next: (docs: any) => {
        const d: any[] = Array.isArray(docs) ? docs : [];
        const recruit: any = { ...r, docs: d };
        const recruitedBy = (recruit && recruit.recruited_by) ? ('' + recruit.recruited_by).trim() : '';
        const normalizedDocs: any[] = d.map((x: any) => ({
          ...x,
          status: (x && x.status) ? x.status : x?.status
        }));
        this.data.getData(`quizzes/user-quizzes`, { params: { user_id: r.id } }).subscribe((quizzes: any) => {
          const rows: any[] = Array.isArray(quizzes?.data) ? quizzes.data : Array.isArray(quizzes) ? quizzes : [];
          const completedQuizzes = rows.filter((q: any) => q?.completed);
          const pendingQuizzes = rows.filter((q: any) => !q?.completed);

          const recruitWithQuizzes: any = { ...recruit, quizzes: rows };

          const profileSummary: any[] = [
            { key: 'email', label: 'Email', value: recruit.email, pendingMessage: 'Rep needs to enter email!' },
            { key: 'date_of_birth', label: 'Date of Birth', value: this.getDateOfBirth(recruit), pendingMessage: 'Rep needs to enter date of birth!' },
            { key: 'shirtSize', label: 'Shirt Size', value: (recruit.shirtSize ?? recruit.shirt_size), pendingMessage: 'Rep needs to enter shirt size!' },
            { key: 'headshot', label: 'Badge Photo', value: (recruit.headshotUrl ?? recruit.headshot_url), pendingMessage: 'Rep needs to upload badge photo' }
          ];

          const outstandingDocs = normalizedDocs.filter((x: any) => ('' + (x?.status || '')).toLowerCase() !== 'completed');

          const outstandingItems = [
            ...profileSummary.filter((i: any) => !i.value).map((i: any) => ({
              label: i.label,
              message: i.pendingMessage
            })),
            ...outstandingDocs.map((x: any) => ({
              label: x && x.name ? x.name : 'Document',
              message: 'This document is not completed!'
            })),
            ...pendingQuizzes.map((q: any) => ({
              label: q?.name || 'Quiz',
              message: 'Quiz not completed yet.'
            }))
          ];

          const comp: any = this.getCompletionPercent(recruitWithQuizzes);
          const completion = comp && comp.completion ? comp.completion : 0;

          this.selectedRecruit = {
            completion: completion,
            r: r,
            docs: normalizedDocs,
            quizzes: rows,
            profileSummary: profileSummary,
            outstandingItems: outstandingItems,
            completedDocs: normalizedDocs.filter((x: any) => ('' + (x?.status || '')).toLowerCase() === 'completed'),
            completedQuizzes: completedQuizzes,
            recruitedBy: recruitedBy
          };
          console.log('selectedRecruit', this.selectedRecruit);
          this.recruitModal = true;
        });
      },
      error: (e: any) => {
        console.error(e)
      }
    })
  }

  getCompletionPercent(recruit: any) {
    const profileChecks = [
      !!recruit?.email,
      !!this.getDateOfBirth(recruit),
      !!(recruit?.shirtSize ?? recruit?.shirt_size),
      !!(recruit?.headshotUrl ?? recruit?.headshot_url)
    ];

    const docs = Array.isArray(recruit?.docs) ? recruit.docs : [];
    const completedDocs = docs.filter((d: any) => String(d?.status).toLowerCase() === 'completed').length;

    const quizzes = Array.isArray(recruit?.quizzes) ? recruit.quizzes : [];
    const completedQuizzes = quizzes.filter((q: any) => q?.completed).length;

    const completed =
      profileChecks.filter(Boolean).length +
      completedDocs +
      completedQuizzes;

    const total = profileChecks.length + docs.length + quizzes.length;

    const completion = total ? Math.round((completed / total) * 100) : 0;

    return { completion };
  }

  applyRecruitFilter() {
    const normalizedTerm = (this.recruitSearchControl.value || '').trim().toLowerCase();
    const statusFilter = this.statusFilterControl.value || 'all';

    if (!this.myRecruits?.length) {
      this.filteredRecruits = [];
      return;
    }

    this.filteredRecruits = this.myRecruits.filter((recruit: any) => {
      const fieldsToSearch = [
        recruit?.rep,
        recruit?.first_name,
        recruit?.last_name,
        recruit?.phone,
        recruit?.email
      ];

      const matchesTerm = !normalizedTerm || fieldsToSearch.some((field) =>
        String(field || '').toLowerCase().includes(normalizedTerm)
      );

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'completed'
          ? this.recruitLooksDone(recruit)
          : !this.recruitLooksDone(recruit));

      return matchesTerm && matchesStatus;
    });
  }

  hasPermission(permission: string): boolean {
    return this.app.hasPermission(permission);
  }

  setParams() {
    let params = new HttpParams()

    if (this.hasPermission('view_all_users')) {
      params = params.set('all', true);
    } 
    else if(this.hasPermission('view_distributor_users')) {
      params = params.set('distributor_id', this.currentUser.distributor_id);
    } 
    else if(this.hasPermission('view_company_users')) {
      params = params.set('company_id', this.currentUser.company_id);
    } 
    else if (this.currentUser?.viewable_groups?.length > 0) {
      params = params.set('viewable_groups', this.currentUser.viewable_groups);
    } 
    else {
      params = params.set('my_id', this.currentUser.id);
    }

    return params;
  }

  private getDateOfBirth(recruit: any): string | null {
    const dob = recruit?.date_of_birth;
    if (!dob) return null;
    const dobStr = String(dob);
    const isoMatch = dobStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${month}/${day}/${year}`;
    }
    const date = new Date(dob);
    if (isNaN(date.getTime())) return null;
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  }

  displayDateOfBirth(recruit: any): string {
    return this.getDateOfBirth(recruit) || '--';
  }

  recruitLooksDone(r: any) {
    if (r && typeof r.recruitmentComplete === 'boolean') {
      return r.recruitmentComplete;
    }
    return !!r?.onboardingCompleted;
  }

}
